import { useState } from "react";
import { markBackupSaved } from "../core/backup";
import type { Exercise } from "../core/exercises";
import type { Backend } from "../core/storage";
import type { Template } from "../core/templates";
import {
  applyImport, changedFields, choiceKey, exportBackup, exportLibrary, fileName, isEmptyPlan, planImport, readTransferFile, restoreBackup,
  type Choice, type Choices, type ImportPlan, type ImportReport, type TransferFile,
} from "../core/transfer";
import { platform, saveJson } from "../platform";
import { useBack } from "./back";
import { ConfirmDialog } from "./ConfirmDialog";
import { formatDate, t } from "./texts";

type Picking = "import" | "restore";

/** Export and import of glossary and templates, and the full backup with its restore. */
export function DataPage(props: {
  db: Backend;
  exercises: Exercise[];
  templates: Template[];
  /** a workout is running: no restore */
  busy: boolean;
  onBack(): void;
  onChanged(): Promise<void>;
}) {
  const { db } = props;
  const [notice, setNotice] = useState<string[]>();
  const [review, setReview] = useState<ImportPlan>();
  const [restore, setRestore] = useState<TransferFile>();
  useBack(props.onBack);

  const save = async (file: TransferFile) => {
    const name = fileName(file.kind, file.exportedAt);
    try {
      const saved = await saveJson(name, file);
      setNotice([saved ? t.data.saved(name) : t.data.notSaved]);
      if (saved && file.kind === "backup") { await markBackupSaved(db, Date.now()); await props.onChanged(); }
    } catch (e) {
      setNotice([t.data.saveFailed(e instanceof Error ? e.message : String(e))]);
    }
  };
  const pick = async (what: Picking) => {
    setNotice(undefined);
    const text = await platform.openTextFile();
    if (text !== undefined) await opened(text, what);
  };
  const opened = async (text: string, what: Picking) => {
    const r = await readTransferFile(text);
    if (!r.ok) return setNotice([t.data.readError[r.error]]);
    if (what === "restore") {
      if (r.file.kind !== "backup") return setNotice([t.data.notBackup]);
      return setRestore(r.file);
    }
    // a backup's glossary and templates can be merged as well; its workouts are left out
    const plan = planImport({ exercises: props.exercises, templates: props.templates }, r.file);
    if (isEmptyPlan(plan)) return setNotice([t.data.nothingNew]);
    setReview(plan);
  };
  const doImport = async (choices: Choices) => {
    if (!review) return;
    const report = await applyImport(db, review, choices, t.data.importWord);
    setReview(undefined);
    setNotice(reportLines(report));
    await props.onChanged();
  };
  const doRestore = async () => {
    if (!restore) return;
    const file = restore;
    setRestore(undefined);
    const r = await restoreBackup(db, file);
    // the data on the phone is now the file's: no backup reminder for the restored workouts
    if (r === "restored") await markBackupSaved(db, Date.now());
    setNotice([r === "restored" ? t.data.restored : r === "notBackup" ? t.data.notBackup : t.data.workoutRunning]);
    await props.onChanged();
  };

  if (review) return <ImportReview plan={review} onCancel={() => setReview(undefined)} onImport={doImport} />;

  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={props.onBack}>{t.back}</button>
      </header>
      <h1>{t.data.title}</h1>
      {notice && <div className="notice">{notice.map((l, i) => <p key={i}>{l}</p>)}</div>}

      <section>
        <h2>{t.data.libraryTitle}</h2>
        <p className="muted-text">{t.data.libraryText}</p>
        <div className="action-stack">
          <button className="secondary" onClick={async () => save(await exportLibrary(db, Date.now()))}>{t.data.export}</button>
          <button className="secondary" onClick={() => void pick("import")}>{t.data.import}</button>
        </div>
      </section>

      <section>
        <h2>{t.data.backupTitle}</h2>
        <p className="muted-text">{t.data.backupText}</p>
        <div className="action-stack">
          <button className="primary" onClick={async () => save(await exportBackup(db, Date.now()))}>{t.data.saveBackup}</button>
          <button className="secondary" disabled={props.busy} onClick={() => void pick("restore")}>{t.data.restore}</button>
          {props.busy && <p className="muted-text">{t.data.workoutRunning}</p>}
        </div>
      </section>

      {restore && (
        <ConfirmDialog danger confirm={t.data.restoreConfirm}
          text={t.data.confirmRestore(formatDate(restore.exportedAt), [
            t.data.exercisesCount(restore.exercises.length), t.data.templatesCount(restore.templates.length),
            t.data.sessionsCount(restore.sessions?.length ?? 0),
          ].join(", "))}
          onCancel={() => setRestore(undefined)} onConfirm={doRestore} />
      )}
    </>
  );
}

function reportLines(r: ImportReport): string[] {
  const parts = [
    r.exercisesAdded && t.data.addedPart(t.data.exercisesCount(r.exercisesAdded)),
    r.exercisesReplaced && t.data.replacedPart(t.data.exercisesCount(r.exercisesReplaced)),
    r.templatesAdded && t.data.addedPart(t.data.templatesCount(r.templatesAdded)),
    r.templatesReplaced && t.data.replacedPart(t.data.templatesCount(r.templatesReplaced)),
  ].filter(Boolean) as string[];
  return [parts.length ? t.data.imported(parts.join(", ")) : t.data.nothingNew, ...r.renamed.map(([a, b]) => t.data.renamed(a, b))];
}

/** What the file brings, and per changed entry the choice between this device's version and the import's. */
function ImportReview(props: { plan: ImportPlan; onCancel(): void; onImport(choices: Choices): void }) {
  const { plan } = props;
  const conflicts = [
    ...plan.exercises.conflicts.map((c) => ({ key: choiceKey("exercise", c.mine.id), kind: t.data.exerciseKind, c })),
    ...plan.templates.conflicts.map((c) => ({ key: choiceKey("template", c.mine.id), kind: t.data.templateKind, c })),
  ];
  const [choices, setChoices] = useState<Choices>({});
  useBack(props.onCancel);
  const all = (choice: Choice) => setChoices(Object.fromEntries(conflicts.map((x) => [x.key, choice])));
  const added = [
    plan.exercises.add.length && t.data.exercisesCount(plan.exercises.add.length),
    plan.templates.add.length && t.data.templatesCount(plan.templates.add.length),
  ].filter(Boolean).join(", ");
  const same = plan.exercises.same + plan.templates.same;

  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={props.onCancel}>{t.cancel}</button>
      </header>
      <h1>{t.data.reviewTitle}</h1>
      {added && <p>{t.data.newEntries(added)}</p>}
      {same > 0 && <p className="muted-text">{t.data.sameEntries(same)}</p>}
      {plan.exercises.matched.length > 0 && (
        <p className="muted-text">{t.data.matchedEntries(plan.exercises.matched.map((m) => m.mine.name).join(", "))}</p>
      )}

      {conflicts.length > 0 && (
        <>
          <p>{t.data.conflictsHead(conflicts.length)}</p>
          {conflicts.length > 1 && (
            <div className="row">
              <button className="secondary" onClick={() => all("mine")}>{t.data.allMine}</button>
              <button className="secondary" onClick={() => all("theirs")}>{t.data.allImport}</button>
            </div>
          )}
          {conflicts.map(({ key, kind, c }) => {
            const choice = choices[key] ?? "mine";
            const renamed = c.mine.name !== c.theirs.name;
            return (
              <section key={key} className="ex-card">
                <div className="name">{kind}: {c.mine.name}{renamed && ` → ${c.theirs.name}`}</div>
                <p className="muted-text">{t.data.changed(changedFields(c).map((f) => t.data.field[f] ?? f).join(", "))}</p>
                <div className="segmented">
                  {(["mine", "theirs"] as const).map((k) => (
                    <button key={k} className={choice === k ? "active" : ""} onClick={() => setChoices({ ...choices, [key]: k })}>
                      {k === "mine" ? t.data.keepMine : t.data.takeImport}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}

      <div className="action-stack">
        <button className="primary" onClick={() => props.onImport(choices)}>{t.data.doImport}</button>
      </div>
    </>
  );
}
